/**
 * Anomaly Detection - Identify unusual patterns in retailer data
 * 
 * Detects:
 * - Large revenue drops (>20% WoW)
 * - SKU count changes (>15% WoW)
 * - Store count changes
 * - Zero revenue when had revenue last week
 */

export interface Anomaly {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  retailer_name?: string;
  title: string;
  description: string;
  metric: 'revenue' | 'units' | 'skus' | 'stores';
  change_percent?: number;
}

export interface KPIData {
  totalRevenue: number;
  totalUnits: number;
  activeSKUs: number;
  storeCount: number;
  weekOverWeek?: {
    revenue?: number | null;
    units?: number | null;
    activeSKUs?: number | null;
    storeCount?: number | null;
  };
}

export interface RetailerData {
  retailer_name: string;
  revenue: number | null;
  units: number | null;
  skus: number | null;
  stores: number | null;
  hasData: boolean;
}

/**
 * Detect anomalies in overall KPI data
 */
export function detectKPIAnomalies(kpis: KPIData): Anomaly[] {
  const anomalies: Anomaly[] = [];

  // Revenue drop >20%
  if (kpis.weekOverWeek?.revenue !== null && kpis.weekOverWeek?.revenue !== undefined) {
    if (kpis.weekOverWeek.revenue < -0.20) {
      anomalies.push({
        id: `kpi-revenue-drop-${Date.now()}`,
        severity: 'critical',
        title: 'Significant Revenue Drop',
        description: `Total revenue down ${Math.abs(kpis.weekOverWeek.revenue * 100).toFixed(1)}% vs last week`,
        metric: 'revenue',
        change_percent: kpis.weekOverWeek.revenue
      });
    }
  }

  // Large revenue increase >30% (also notable)
  if (kpis.weekOverWeek?.revenue !== null && kpis.weekOverWeek?.revenue !== undefined) {
    if (kpis.weekOverWeek.revenue > 0.30) {
      anomalies.push({
        id: `kpi-revenue-surge-${Date.now()}`,
        severity: 'info',
        title: 'Revenue Surge Detected',
        description: `Total revenue up ${(kpis.weekOverWeek.revenue * 100).toFixed(1)}% vs last week`,
        metric: 'revenue',
        change_percent: kpis.weekOverWeek.revenue
      });
    }
  }

  // SKU count change >15%
  if (kpis.weekOverWeek?.activeSKUs !== null && kpis.weekOverWeek?.activeSKUs !== undefined) {
    const change = Math.abs(kpis.weekOverWeek.activeSKUs);
    if (change > 0.15) {
      anomalies.push({
        id: `kpi-sku-change-${Date.now()}`,
        severity: 'warning',
        title: `${kpis.weekOverWeek.activeSKUs < 0 ? 'SKU Discontinuations' : 'New SKUs Added'}`,
        description: `Active SKU count ${kpis.weekOverWeek.activeSKUs < 0 ? 'decreased' : 'increased'} ${(change * 100).toFixed(1)}% vs last week`,
        metric: 'skus',
        change_percent: kpis.weekOverWeek.activeSKUs
      });
    }
  }

  // Store count unexpected change
  if (kpis.weekOverWeek?.storeCount !== null && kpis.weekOverWeek?.storeCount !== undefined) {
    const change = Math.abs(kpis.weekOverWeek.storeCount);
    if (change > 0.10) {
      anomalies.push({
        id: `kpi-store-change-${Date.now()}`,
        severity: 'warning',
        title: 'Store Count Changed',
        description: `Total stores ${kpis.weekOverWeek.storeCount < 0 ? 'decreased' : 'increased'} ${(change * 100).toFixed(1)}% vs last week`,
        metric: 'stores',
        change_percent: kpis.weekOverWeek.storeCount
      });
    }
  }

  return anomalies;
}

/**
 * Detect anomalies in per-retailer data
 */
export function detectRetailerAnomalies(
  retailers: RetailerData[],
  previousWeekRetailers?: RetailerData[]
): Anomaly[] {
  const anomalies: Anomaly[] = [];

  retailers.forEach(retailer => {
    // Check for retailers with no data uploaded
    if (!retailer.hasData) {
      anomalies.push({
        id: `retailer-no-data-${retailer.retailer_name}`,
        severity: 'info',
        retailer_name: retailer.retailer_name,
        title: `${retailer.retailer_name}: No Data`,
        description: 'Upload data to unlock insights for this retailer',
        metric: 'revenue'
      });
    }

    // Check for zero revenue when retailer has data
    if (retailer.hasData && (!retailer.revenue || retailer.revenue === 0)) {
      anomalies.push({
        id: `retailer-zero-revenue-${retailer.retailer_name}`,
        severity: 'critical',
        retailer_name: retailer.retailer_name,
        title: `${retailer.retailer_name}: Zero Revenue`,
        description: 'Retailer has data but no revenue recorded this week',
        metric: 'revenue'
      });
    }
  });

  return anomalies;
}

/**
 * Get all anomalies (combined KPI and retailer anomalies)
 */
export function getAllAnomalies(
  kpis: KPIData,
  retailers: RetailerData[],
  previousWeekRetailers?: RetailerData[]
): Anomaly[] {
  const kpiAnomalies = detectKPIAnomalies(kpis);
  const retailerAnomalies = detectRetailerAnomalies(retailers, previousWeekRetailers);
  
  return [...kpiAnomalies, ...retailerAnomalies];
}

